import { useState } from "react";
import { useApiClient } from "../api/ApiContext.jsx";
import { useAuth } from "../auth/AuthContext.jsx";
import { HelpForm } from "../components/index.js";
import { InlineMessage, PageHeader, errorText } from "../ui/Feedback.jsx";

/**
 * The help screen.
 *
 * Two fields and a transport. The screen owns none of the addressing: the API
 * sends to the deployment's support mailbox and sets Reply-To from the session,
 * so the only thing this form decides is what the message says. That is why
 * there is no "your email" field here — asking for an address we already know
 * would invite a customer to type a different one and then wonder why the answer
 * went to their account.
 *
 * The success state replaces the form rather than sitting above it: a form that
 * stays filled in after a send invites a second submission of the same message,
 * and there is no way to take one back out of a mailbox.
 */
export default function HelpScreen() {
  const api = useApiClient();
  const { user } = useAuth();
  // "idle" | "submitting" | "sent". A single value rather than two booleans, so
  // "sending" and "sent" cannot both be true after a slow reply lands.
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  async function handleSubmit({ subject, body }) {
    setStatus("submitting");
    setError(null);
    try {
      const { accepted } = await api.support.contact({ subject, body });
      if (!accepted) {
        // The endpoint answers 202 with an explicit status instead of an empty
        // body, so a 2xx we cannot read is not a delivery. Guessing "sent" here
        // would tell the customer to wait for a reply that was never requested.
        setError("We couldn't confirm your message was accepted. Please try again.");
        setStatus("idle");
        return;
      }
      setStatus("sent");
    } catch (err) {
      // 503 not_configured means this deployment has no mail transport at all
      // and retrying cannot help; 502 means the transport refused and it can.
      // `errorText` keeps the server's own sentence, which says which happened.
      setError(errorText(err, "We couldn't send your message. Please try again."));
      setStatus("idle");
    }
  }

  if (status === "sent") {
    return (
      <div style={{ maxWidth: 640 }}>
        <PageHeader title="Help" subtitle="Get in touch with the people who run this service." />
        <InlineMessage tone="success">
          Thanks — your message is on its way to support.
        </InlineMessage>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 16px" }}>
          {user?.email
            ? `We'll reply to ${user.email}, the address on your account.`
            : "We'll reply to the address on your account."}
        </p>
        <button
          type="button"
          className="ical-btn"
          onClick={() => {
            setError(null);
            setStatus("idle");
          }}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <PageHeader
        title="Help"
        subtitle="Tell us what went wrong and we'll reply to your account's address."
      />
      {error ? <InlineMessage tone="danger">{error}</InlineMessage> : null}
      <HelpForm onSubmit={handleSubmit} submitting={status === "submitting"} />
    </div>
  );
}
